import { signEd25519, verifyEd25519, type SigningKey } from './keys.js'

/**
 * DSSE (Dead Simple Signing Envelope) — https://github.com/secure-systems-lab/dsse
 * Envelope and PAE encoding must match the marketplace registry's verifier:
 * payload type `application/vnd.verentis.package.digests+json`, Ed25519 signatures.
 */
export const DSSE_PAYLOAD_TYPE = 'application/vnd.verentis.package.digests+json'

export interface DsseEnvelope {
  payload: string
  payloadType: string
  signatures: { keyid: string; sig: string }[]
}

/** DSSE pre-authentication encoding: "DSSEv1 <typeLen> <type> <payloadLen> " + payload. */
export function preAuthenticationEncoding(payloadType: string, payload: Buffer): Buffer {
  const typeBytes = Buffer.from(payloadType, 'utf8')
  const header = Buffer.from(
    `DSSEv1 ${typeBytes.length} ${payloadType} ${payload.length} `, 'utf8')
  return Buffer.concat([header, payload])
}

export function signEnvelope(payload: string, key: SigningKey): DsseEnvelope {
  const payloadBytes = Buffer.from(payload, 'utf8')
  const pae = preAuthenticationEncoding(DSSE_PAYLOAD_TYPE, payloadBytes)
  const signature = signEd25519(key.privateKeyPem, pae)
  return {
    payload: payloadBytes.toString('base64'),
    payloadType: DSSE_PAYLOAD_TYPE,
    signatures: [{ keyid: key.kid, sig: signature.toString('base64') }],
  }
}

/** Returns the keyid of the first signature that verifies, or null. */
export function verifyEnvelope(envelope: DsseEnvelope, expectedPayload: string, publicKeysByKid: Record<string, string>): string | null {
  if (envelope.payloadType !== DSSE_PAYLOAD_TYPE) return null
  const payloadBytes = Buffer.from(envelope.payload, 'base64')
  if (payloadBytes.toString('utf8') !== expectedPayload) return null

  const pae = preAuthenticationEncoding(envelope.payloadType, payloadBytes)
  for (const signature of envelope.signatures) {
    for (const [kid, publicKey] of Object.entries(publicKeysByKid)) {
      if (signature.keyid && signature.keyid !== kid) continue
      try {
        if (verifyEd25519(publicKey, pae, Buffer.from(signature.sig, 'base64'))) return kid
      } catch {
        // try next key
      }
    }
  }
  return null
}
